import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { CustomFormComponent } from '@bo-client/activation-management/custom-form.component';
import { CreateGridRegistrationFormsService } from '@bo-client/activation-management/grid-registration/create-grid-registration-forms.service';
import { CreateFormsGrPlaceinserviceService } from '@bo-client/activation-management/grid-registration/forms/create-forms-gr-placeinservice.service';
import { p14aApplies } from '@bo-client/activation-management/grid-registration/grid-registration.utils';
import {
  ConsumerFormV2,
  ConsumerModel
} from '@bo-client/activation-management/grid-registration/model/consumer.model';
import {
  FeederConnectionStandard,
  FeedingPlantTypeEnum,
  NaProtectionTypeEnum,
  PowerGenerationSourceEnum,
  ReactivePowerProvisioningMethod,
  SymmetryConditionsEnum
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import {
  GridRegistrationModel,
  PlaceInServiceModel,
  PlaceInServiceRequestForm
} from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import {
  ProducerForm,
  ProducerModel
} from '@bo-client/activation-management/grid-registration/model/producer.model';
import {
  PVModuleForm,
  PVModuleModel
} from '@bo-client/activation-management/grid-registration/model/pv-module.model';
import {
  StorageForm,
  StorageModel
} from '@bo-client/activation-management/grid-registration/model/storage.model';
import { ErrorFormCommissioningEnum } from '@bo-client/activation-management/shared/forms/create-forms-errors-model';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import { CustomValidators } from '@bo-client/activation-management/shared/forms/validators';
import { DataForCustomFormModel } from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { MetaService } from '@bo-client/core/api/meta-service';
import { ShowErrorModel } from '@bo-client/shared/form-errors/errors-form-model';
import { ErrorsFormsService } from '@bo-client/shared/form-errors/errors-forms.service';
import { TypedForm } from '@bo-client/shared/forms/generic-forms/typedForm.type';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';
import { UploadForm } from '@bo-client/shared/models/documentDto';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MastrPvPlantWithLocationDto } from '@bo-client/activation-management/shared/components/mastr-validation/MastrData.dto';
import { MASTR_NUMBER_MISMATCH_TAG } from '@bo-client/activation-management/shared/components/mastr-validation/mastr-validation.component';
import { ToastrService } from 'ngx-toastr';
import { RegexToCheckModuleMaStrNumber } from '@bo-client/shared/forms/forms.validators';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import { UiAdaptionsModel } from '@bo-client/tenant-management/models/ui-adaptions.model';

/**
 * UI-Adaption der frei konfigurierbaren Pflicht-Bedingung am Ende des Protokolls. Das
 * Speicher-Protokoll hat einen eigenen Anchor, damit beide getrennt schaltbar sind.
 */
export const CUSTOM_CONDITIONS_CHECKBOX_ANCHOR =
  'gridRegistration.placeInServiceRequest.feederConditionsCheckbox';

@Component({
  selector: 'app-gr-place-in-service-feeder-request-form',
  templateUrl: './gr-place-in-service-feeder-request-form.component.html',
  styleUrls: ['./gr-place-in-service-feeder-request-form.component.scss']
})
export class GrPlaceInServiceFeederRequestFormComponent
  implements CustomFormComponent<GridRegistrationModel, GridRegistrationModel>, OnInit
{
  private destroyRef = inject(DestroyRef);
  createFormsGrPlaceinServiceConfirmationService = inject(CreateFormsGrPlaceinserviceService);
  private createFormsGridRegistrationService = inject(CreateGridRegistrationFormsService);
  private createFormsService = inject(CreateFormsService);
  dataShareService = inject(DataShareService);
  metaService = inject(MetaService);
  private fb = inject(FormBuilder);
  private errorsFormsService = inject(ErrorsFormsService);
  private toastrService = inject(ToastrService);
  private validatorsService = inject(ValidatorsService);

  /**
   * Frei konfigurierbare Bedingung aus dem Karte "Weitere Bedingungen". Das Control liegt
   * bewusst außerhalb von `formGroup`: `confirm()` serialisiert die FormGroup über
   * `getRawValue()`, die Bestätigung wird aber nicht persistiert — der Abschluss des
   * Prozessschritts impliziert sie.
   */
  customConditionsCheckbox = new FormControl(false);

  @Input() data: DataForCustomFormModel<GridRegistrationModel>;
  @Output() onConfirm: EventEmitter<GridRegistrationModel> = new EventEmitter();

  formGroup: PlaceInServiceRequestForm;
  naProtectionTypeEnum = NaProtectionTypeEnum;
  symmetryConditionsEnum = SymmetryConditionsEnum;
  totalActivePower = 0;
  totalApparentPower = 0;
  totalModulePower = 0;
  errorFormList: ShowErrorModel[] = [];
  errorFieldRequired = 'Feld ist erforderlich';
  errorOptionRequired = 'Bitte wählen Sie eine Option aus';
  errorCheckRequired = 'Bitte bestätigen Sie die Bedingung';
  model: GridRegistrationModel;

  mastrValidationActive$ = this.dataShareService
    .getSingleTenantConfig$<boolean>(
      'gridRegistration.mastrValidationConfig.enableMastrValidationForPlaceInServiceRequest'
    )
    .pipe(takeUntilDestroyed(this.destroyRef));

  mastrMismatchFields: string[] = [];

  showExternalIdField$ = of(false);
  showExternalCustomerIdField$ = of(false);

  totalConsumerStoragePower$: Observable<number>;

  formValue$: Observable<PlaceInServiceRequestForm['value']>;
  modelAndFormValue$: Observable<GridRegistrationModel>;

  /** Local calendar day key (YYYY-MM-DD) from MaStR after last `updateFormWithMastrData`; used for mismatch warning. */
  private readonly mastrPlaceInServiceDatumExpectedCalendarKey$ = new BehaviorSubject<string | null>(null);

  /** Warning text when the in-service date differs from the value last synced from MaStR (null = no warning). */
  placeInServiceDatumMastrWarning$: Observable<string | null>;

  readonly storageMaStRDisplayData = {
    inbetriebnahmeDatum: null as Date,
    registerDate: null as Date
  };

  get placeInForm() {
    return this.formGroup.get('placeInService') as FormGroup;
  }

  get producers() {
    return this.formGroup.controls['producers'] as FormArray;
  }

  get pvModules() {
    return this.formGroup.controls['pvModules'] as FormArray;
  }

  get storages() {
    return this.formGroup.controls['storages'] as FormArray;
  }
  // Wird verwendet, da momentan nur ein Speicher an einer Netzanmeldung hängen kann
  get firstStorage() {
    return this.storages.at(0) as TypedForm<StorageModel>;
  }

  get consumers() {
    return this.formGroup.controls['consumers'] as FormArray;
  }
  // Speicher als Verbraucher
  get firstConsumer() {
    return this.consumers.at(0) as TypedForm<ConsumerModel>;
  }

  get uploads(): FormArray {
    return this.formGroup.get('uploads') as FormArray;
  }

  get meterOne() {
    return this.formGroup.get('meterOne');
  }

  get meterTwo() {
    return this.formGroup.get('meterTwo');
  }

  get meterThree() {
    return this.formGroup.get('meterThree');
  }

  get meteringConcept() {
    return this.formGroup.get('meteringConcept.id') as FormControl;
  }

  get feederConnectionStandard() {
    return this.model?.feederConnectionStandard;
  }

  /** Wie in gr-custom-approve: cos φ ist nur bei nicht-kennliniengeführten Verfahren sinnvoll. */
  get isQUStandardCurve(): boolean {
    return this.model?.reactivePowerProvisioningMethod === ReactivePowerProvisioningMethod.Q_U_STANDARD_CURVE;
  }

  get initialP14aApplies() {
    const firstConsumer = this.model?.consumers[0];
    return firstConsumer ? p14aApplies('Storage', firstConsumer) : false;
  }

  ngOnInit(): void {
    this.model = this.data.model;

    if (this.model.producers && this.model.producers.length > 0 && !this.model.newProduction) {
      this.model.producers.forEach((x) => {
        this.totalActivePower = this.totalActivePower + x.acActivePowerMax * x.count;
        this.totalApparentPower = this.totalApparentPower + x.acApparentPowerMax * x.count;
      });

      /**
       * Rückwärtskompatibilität: ältere Vorgänge ohne `newProduction` –
       * Wert aus den verbauten Erzeugern (Scheinleistung) ableiten.
       */
      this.model.newProduction = this.totalApparentPower;
    }
    if (this.model.pvModules && this.model.pvModules.length > 0 && !this.model.newInstalledPower) {
      this.model.pvModules.forEach((x) => {
        this.totalModulePower = this.totalModulePower + (x.modulePowerWatt * x.count) / 1000;
      });

      /**
       * Rückwärtskompatibilität: ältere Vorgänge ohne `newInstalledPower` –
       * Wert aus den PV-Modulen ableiten.
       */
      this.model.newInstalledPower = this.totalModulePower;

      //  this.setFieldValidator('effectiveModulePowerWatt', 'required', true, this.totalModulePower);
    }

    this.formGroup = this.fb.group(
      {
        deviceType: [null as number],
        placeInService: this.createFormsGrPlaceinServiceConfirmationService.createPlaceInServiceRequestForm(
          new PlaceInServiceModel(),
          !this.model?.disableMastrValidation &&
            this.dataShareService.tenantConfig?.gridRegistration?.mastrValidationConfig
              ?.enableMastrValidationForPlaceInServiceRequest &&
            !this.dataShareService.tenantConfig?.gridRegistration?.mastrValidationConfig
              ?.allowPlaceInServiceDateEdit
        ),
        newProduction: [null as number],
        newInstalledPower: [null as number],
        // Detaillierte Aufschlüsselung von Modulen / Wechselrichtern
        pvModules: this.fb.array<PVModuleForm>([]),
        producers: this.fb.array<ProducerForm>([]),
        storages: this.fb.array<StorageForm>([]),
        consumers: this.fb.array<ConsumerFormV2>([]),
        uploads: this.fb.array<UploadForm>([]),
        meterNumber: ['', Validators.required],
        meterOne: this.fb.group({ meterNo: [''], value: [''], obisCode: [''] }),
        meterTwo: this.fb.group({ meterNo: [''], value: [''], obisCode: [''] }),
        meterThree: this.fb.group({ meterNo: [''], value: [''], obisCode: [''] }),
        meteringConcept: this.fb.group({ id: [null as number, Validators.required] }),
        /*feedInManagementSerialNumber: [''],*/
        maStRNumberProducer: [
          '',
          ...(!this.model?.disableMastrValidation &&
          this.dataShareService.tenantConfig?.gridRegistration?.mastrValidationConfig
            ?.enableMastrValidationForPlaceInServiceRequest
            ? [[Validators.required, Validators.pattern(RegexToCheckModuleMaStrNumber)]]
            : [])
        ],
        maStRSSENumberStorage: [''],
        maStRNumberStorage: this.fb.control({
          value: '',
          disabled: true
        }),
        maStREEGNumber: this.fb.control({
          value: '',
          disabled: true
        }),
        maStRNumber: [''],
        maStRAcceptedDeclaration: [false],
        maStRRegistrationDate: this.fb.control({
          value: null as Date,
          disabled:
            !this.model?.disableMastrValidation &&
            !!this.dataShareService.tenantConfig?.gridRegistration?.mastrValidationConfig
              ?.enableMastrValidationForPlaceInServiceRequest
        }),
        customer: this.createFormsService.createContactDataFormV2({}, false)
      },
      {
        validators: [
          CustomValidators.createPVModulePowerValidatorSync(
            this.dataShareService.tenantConfig?.gridRegistration?.allowedPowerDeviationPercent,
            {
              isPhotovoltaic: this.model.powerGenerationSource === PowerGenerationSourceEnum.PhotoVoltaic
            }
          )
        ],
        updateOn: 'change'
      }
    );

    /*this.showFeedInManagement$ = this.dataShareService
      .getSingleTenantConfig$('gridRegistration.showFeedInManagementForPlaceInServiceRequest')
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        switchMap((config) => {
          if (!config) return of(false);
          return this.formGroup.controls.pvModules.valueChanges.pipe(
            startWith(this.formGroup.controls.pvModules.value),
            map((pvModules) => {
              const installedPower = pvModules
                .map((item) => item.count * item.modulePowerWatt)
                .reduce((acc, cur) => acc + cur, 0);
              return installedPower / 1000 > 25;
            })
          );
        }),
        distinctUntilChanged(),
        tap((value) => {
          const feedInManagementSerialNumberControl = this.formGroup.controls.feedInManagementSerialNumber;
          if (!value) {
            feedInManagementSerialNumberControl.setValidators(null);
          } else {
            feedInManagementSerialNumberControl.setValidators([Validators.required]);
          }
          feedInManagementSerialNumberControl.updateValueAndValidity();
        }),
        shareReplay(1)
      );*/

    const placeInServiceDatumControl = this.placeInForm.get('placeInServiceDatum')!;
    this.placeInServiceDatumMastrWarning$ = combineLatest([
      this.mastrPlaceInServiceDatumExpectedCalendarKey$,
      placeInServiceDatumControl.valueChanges.pipe(
        startWith(placeInServiceDatumControl.value),
        map((v) => placeInServiceDatumCalendarKey(v)),
        distinctUntilChanged()
      )
    ]).pipe(
      map(([expectedKey, currentKey]) =>
        expectedKey != null && currentKey !== expectedKey
          ? 'Das von Ihnen eingetragene Datum der EEG-technischen Inbetriebnahme stimmt nicht mit dem Datum im MaStR überein. Bitte stimmen Sie das Datum ggf. mit dem Anlagenbetreiber ab.'
          : null
      ),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef)
    );

    this.initShowExternalIdFields();

    this.initArrayForms();

    // nochmal alles reinbügeln
    this.formGroup.patchValue(this.model, { emitEvent: true });

    this.formValue$ = this.formGroup.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.formGroup.value),
      shareReplay(1)
    );

    this.modelAndFormValue$ = this.formValue$.pipe(
      map((formValue) => ({ ...this.model, ...formValue } as GridRegistrationModel))
    );

    this.totalConsumerStoragePower$ = this.formValue$.pipe(
      map((formValue) => {
        return formValue.consumers[0]?.power && formValue.storages[0]?.count
          ? Number(formValue.consumers[0].power) * Number(formValue.storages[0].count)
          : Number.NaN;
      })
    );

    combineLatest([
      this.dataShareService.getSingleTenantConfig$<boolean>(
        'gridRegistration.storageConfig.requireStorageCapacityForFeeder'
      ),
      this.storages.valueChanges.pipe(startWith(this.storages.value))
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([required]) => {
        const control = this.firstStorage?.get('usableCapacity');
        if (!control) {
          return;
        }
        if (required === true) {
          control.setValidators([Validators.required]);
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity({ emitEvent: false });
      });

    this.dataShareService.uiAdaptions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((uiAdaptions) => {
      const invoiceMeteringCheckDoneOptional =
        uiAdaptions.find(
          (adaption) =>
            adaption.anchor === 'gridRegistration.placeInServiceRequest.invoiceMeteringCheckDoneOptional'
        )?.value || 'false';
      const powerDirectionSensorCheckDone =
        uiAdaptions.find(
          (adaption) =>
            adaption.anchor === 'gridRegistration.placeInServiceRequest.powerDirectionSensorCheckDoneOptional'
        )?.value || 'false';

      if (!this.model?.feederConnectionStandard || this.model?.feederConnectionStandard === 'VDE4105') {
        this.setFieldValidator('effectiveAndRequestedValuesMatch', 'mustTrue');
        if (invoiceMeteringCheckDoneOptional === 'true' || invoiceMeteringCheckDoneOptional === 'show') {
          this.setFieldValidator('invoiceMeteringCheckDone', 'optional');
        } else {
          this.setFieldValidator('invoiceMeteringCheckDone', 'mustTrue');
        }
        this.setFieldValidator('vdeCertificateExists', 'mustTrue');
        if (powerDirectionSensorCheckDone === 'true' || powerDirectionSensorCheckDone === 'show') {
          this.setFieldValidator('powerDirectionSensorCheckDone', 'optional');
        } else {
          this.setFieldValidator('powerDirectionSensorCheckDone', 'mustTrue');
        }
        this.setFieldValidator('vdeArN4100_55_CertificateExists', 'required');
        this.setFieldValidator('powerProtectionCertificateExists', 'mustTrue');
        this.setFieldValidator('factorCosphiCheck', 'mustTrue');
        this.setFieldValidator('naProtection', 'required');
        this.setFieldValidator('overvoltageProtectionType', 'required');
        this.setFieldValidator('pMonitoringExists', 'required');
        this.setFeedInManagementValidators();
        this.setFieldValidator('symmetryConditions', 'required');
        this.setFieldValidator('placeInServiceDatum', 'required');
        this.setFieldValidator('vdeConditions', 'mustTrue');
      }

      // Bewusst außerhalb des VDE4105-Blocks: die zusätzliche Bedingung gilt für jeden
      // Anschlussstandard.
      this.updateCustomConditionsCheckboxValidation(
        uiAdaptions.find((adaption) => adaption.anchor === CUSTOM_CONDITIONS_CHECKBOX_ANCHOR)
      );
    });
  }

  /**
   * Setzt die Pflicht-Validierung der frei konfigurierbaren Bedingung, solange die zugehörige
   * UI-Adaption aktiv ist. Ist sie inaktiv oder nicht hinterlegt, bleibt die Checkbox ohne
   * Validator und blockiert das Absenden nicht.
   */
  private updateCustomConditionsCheckboxValidation(checkboxAdaption: UiAdaptionsModel | undefined): void {
    if (
      checkboxAdaption &&
      (checkboxAdaption.visibility === 'show' || checkboxAdaption.visibility === 'true')
    ) {
      this.customConditionsCheckbox.setValidators(this.validatorsService.checkboxRequiredValidator());
    } else {
      this.customConditionsCheckbox.clearValidators();
    }
    this.customConditionsCheckbox.updateValueAndValidity();
  }

  /**
   * Setzt die Validatoren für die Felder des Einspeisemanagements,
   * abhängig vom Wert des Feldes feedInManagementInfo
   */
  private setFeedInManagementValidators() {
    if (this.feedInManagementFieldsRequired) {
      this.setFieldValidator('eegConfirmation', 'mustTrue');
      this.setFieldValidator('flawlessFunctionGuarantee', 'mustTrue');
    } else {
      this.setFieldValidator('eegConfirmation', 'optional');
      this.setFieldValidator('flawlessFunctionGuarantee', 'optional');
    }
  }

  get feedInManagementFieldsRequired() {
    return this.model?.feedInManagementInfo !== 'noReduction';
  }

  setValidatorsNaProtection() {
    if (this.placeInForm.get('naProtection')) {
      if (this.placeInForm.get('naProtection').value == NaProtectionTypeEnum.Integrated) {
        this.setFieldValidator('overvoltageReleaseTestDone', 'optional');
        this.setFieldValidator('triggerCircuitTestDone', 'optional');
      }
      if (this.placeInForm.get('naProtection').value == NaProtectionTypeEnum.Centralised) {
        this.setFieldValidator('overvoltageReleaseTestDone', 'mustTrue');
        this.setFieldValidator('triggerCircuitTestDone', 'mustTrue');
      }
      this.placeInForm.get('overvoltageReleaseTestDone').patchValue(null);
      this.placeInForm.get('triggerCircuitTestDone').patchValue(null);
      this.placeInForm.get('overvoltageReleaseTestDone').markAsTouched();
      this.placeInForm.get('triggerCircuitTestDone').markAsTouched();
    }
  }

  setValidatorsMonitoring() {
    if (this.placeInForm.get('pMonitoringExists')) {
      if (this.placeInForm.get('pMonitoringExists').value == false) {
        this.setFieldValidator('paveTestDone', 'optional');
        this.setFieldValidator('effectivePave', 'optional');
      }
      if (this.placeInForm.get('pMonitoringExists').value == true) {
        this.setFieldValidator('paveTestDone', 'mustTrue');
        this.setFieldValidator('effectivePave', 'required');
      }
      this.placeInForm.get('paveTestDone').patchValue(null);
      this.placeInForm.get('effectivePave').patchValue(null);
      this.placeInForm.get('paveTestDone').markAsTouched();
      this.placeInForm.get('effectivePave').markAsTouched();
    }
  }

  setSymmetryConditionsMonitoring() {
    if (this.placeInForm.get('symmetryConditions')) {
      if (
        this.placeInForm.get('symmetryConditions').value !=
        this.symmetryConditionsEnum.SinglePhaseDistribution
      ) {
        this.setFieldValidator('addedGenerationL1', 'optional');
        this.setFieldValidator('addedGenerationL2', 'optional');
        this.setFieldValidator('addedGenerationL3', 'optional');
      }
      if (
        this.placeInForm.get('symmetryConditions').value ==
        this.symmetryConditionsEnum.SinglePhaseDistribution
      ) {
        this.setFieldValidator('addedGenerationL1', 'required');
        this.setFieldValidator('addedGenerationL2', 'required');
        this.setFieldValidator('addedGenerationL3', 'required');
      }
      this.placeInForm.get('addedGenerationL1').patchValue(null);
      this.placeInForm.get('addedGenerationL2').patchValue(null);
      this.placeInForm.get('addedGenerationL3').patchValue(null);
      this.placeInForm.get('addedGenerationL1').markAsTouched();
      this.placeInForm.get('addedGenerationL2').markAsTouched();
      this.placeInForm.get('addedGenerationL3').markAsTouched();
    }
  }

  cancel() {
    this.onConfirm.emit(null);
  }

  confirm(hasMastrMismatch: boolean = false) {
    this.formGroup.markAllAsTouched();
    if (this.isValid() && !hasMastrMismatch) {
      const result = {
        ...this.model,
        ...(this.formGroup.getRawValue() as unknown as Partial<GridRegistrationModel>)
      };
      this.onConfirm.emit(result);
    }
    if (this.hasMastrDataFieldMismatch()) {
      this.toastrService.error(
        'Bitte überprüfen Sie die Tabelle der Markt-Stammdatenregister-Daten. Die rot markierten Felder weisen auf Abweichungen hin, die behoben werden müssen.',
        'Abweichungen von Markt-Stammdatenregister vorhanden'
      );
    }
    if (this.hasInvalidMastrNumber()) {
      this.toastrService.error(
        'Bitte überprüfen Sie die angegebene Markt-Stammdatenregister-Nummer',
        'Ungültige Markt-Stammdatenregister-Nummer'
      );
    }
  }

  /** True, wenn die MaStR-Nummer selbst ungültig, leer, im Ladezustand oder nicht abrufbar ist. */
  hasInvalidMastrNumber(): boolean {
    return this.mastrMismatchFields.includes(MASTR_NUMBER_MISMATCH_TAG);
  }
  /**
   * True, wenn echte Datenabweichungen (jenseits des reinen MaStR-Nummer-Status)
   * vorliegen. Steuert den Toast und die rote Markierung der Vergleichskarte.
   */
  hasMastrDataFieldMismatch(): boolean {
    return this.mastrMismatchFields.some((field) => field !== MASTR_NUMBER_MISMATCH_TAG);
  }

  setFieldValidator(
    fieldName: string,
    action: 'required' | 'optional' | 'mustTrue',
    valueLessOrEqual?: boolean,
    valueToCompare?: number
  ) {
    const selectedControl = this.placeInForm.get(fieldName);
    switch (action) {
      case 'required':
        selectedControl.setValidators(
          !valueLessOrEqual
            ? [Validators.required]
            : [Validators.required, this.lessOrEqual(fieldName, valueToCompare)]
        );
        break;
      case 'optional':
        selectedControl.clearValidators();
        break;
      case 'mustTrue':
        selectedControl.setValidators([Validators.required, this.createFormsService.mustTrue]);
        break;
    }
  }

  lessOrEqual(fieldName: string, valueToCompare: number): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      if (control.errors && !control.errors.lessOrEqual) {
        return;
      }
      if (control.value > valueToCompare) {
        return { lessOrEqual: true };
      } else {
        return null;
      }
    };
  }

  isValid(): boolean {
    this.loadErrorsList();
    this.customConditionsCheckbox.markAllAsTouched();
    return this.formGroup.valid && this.customConditionsCheckbox.valid;
  }

  loadErrorsList() {
    this.errorFormList = this.errorsFormsService.checkTwoLevelFormErrors(
      this.formGroup,
      ErrorFormCommissioningEnum,
      null
    );
  }

  private initArrayForms() {
    // PV-Module nur bei Photovoltaik; sonst kein Platzhalter-Eintrag mit Pflichtvalidatoren
    this.pvModules.clear();
    if (this.model.powerGenerationSource === PowerGenerationSourceEnum.PhotoVoltaic) {
      this.model?.pvModules.forEach((pvModule) => {
        const pvModuleGroup = this.createFormsGridRegistrationService.createPVModuleFormSimple(pvModule);
        this.pvModules.push(pvModuleGroup);
      });
      if (this.pvModules.length === 0) {
        this.pvModules.push(
          this.createFormsGridRegistrationService.createPVModuleFormSimple(new PVModuleModel())
        );
      }
    }

    // Erzeuger
    this.producers.clear();
    this.model?.producers.forEach((producer) => {
      const producerGroup = this.createFormsGridRegistrationService.createProducerFormSimple(
        producer,
        this.model
      );
      this.producers.push(producerGroup);
    });
    if (this.producers.length === 0) {
      this.producers.push(
        this.createFormsGridRegistrationService.createProducerFormSimple(new ProducerModel(), this.model)
      );
    }

    // Speicher
    this.storages.clear();
    if (this.model?.storages.length > 0) {
      this.model?.storages.forEach((storage) => {
        const storageGroup = this.createFormsGridRegistrationService.createStorageFormV2(storage, this.model);
        this.storages.push(storageGroup);
      });
    }

    //Verbraucher
    this.consumers.clear();
    if (this.model?.consumers.length > 0) {
      this.model?.consumers.forEach((consumer, index) => {
        const consumerGroup = this.createFormsGridRegistrationService.createConsumerFormV2(consumer);
        this.consumers.push(consumerGroup);
        if (index === 0) {
          consumerGroup.setValidators(noP14aChange(this.initialP14aApplies));
        }
      });
    }

    // Uploads
    this.uploads.clear();
    this.model?.uploads.forEach((upload) => {
      const uploadFormGroup = this.createFormsService.createUploadForm(upload);
      this.uploads.push(uploadFormGroup);
    });
  }

  private initShowExternalIdFields() {
    const config$ = this.dataShareService.tenantConfig$.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((config) => config?.gridRegistration),
      shareReplay(1)
    );
    this.showExternalIdField$ = config$.pipe(map((config) => !!config?.showExternalIdField));
    this.showExternalCustomerIdField$ = config$.pipe(map((config) => !!config?.showExternalCustomerIdField));
  }

  readonly FeederConnectionStandard = FeederConnectionStandard;
  readonly of = of;
  readonly FeedingPlantTypeEnum = FeedingPlantTypeEnum;
  readonly PowerGenerationSourceEnum = PowerGenerationSourceEnum;

  get documentsUploadInfoUIAdaptionName() {
    return (
      'gridRegistration.' +
      this.model?.gridRegistrationType.valueOf() +
      '.placeInServiceRequest.documentsUploadInfo'
    );
  }

  get mastrHintUIAdaptionName() {
    return 'gridRegistration.placeInServiceRequest.mastrHint';
  }

  setMastrMismatchFields(fields: string[]) {
    if (!!fields?.length) {
      this.mastrMismatchFields = fields;
    } else {
      this.mastrMismatchFields = [];
    }
  }

  updateFormWithMastrData(data: MastrPvPlantWithLocationDto) {
    const updateFormControl = (control: FormControl, data: unknown) => {
      if (control.disabled) {
        control.enable();
        control.setValue(data);
        control.disable();
      } else {
        control.setValue(data);
      }
    };

    updateFormControl(
      this.formGroup.controls.maStRRegistrationDate,
      data?.pvUnit?.registrierungsdatum ? new Date(data?.pvUnit?.registrierungsdatum) : null
    );
    const placeInServiceDatumControl = this.formGroup.controls.placeInService.controls.placeInServiceDatum;
    updateFormControl(
      placeInServiceDatumControl,
      data?.pvUnit?.inbetriebnahmedatum ? new Date(data.pvUnit.inbetriebnahmedatum) : null
    );
    this.mastrPlaceInServiceDatumExpectedCalendarKey$.next(
      placeInServiceDatumCalendarKey(placeInServiceDatumControl.value)
    );
    placeInServiceDatumControl.updateValueAndValidity({ emitEvent: true });
    updateFormControl(
      this.formGroup.controls.maStREEGNumber,
      data?.pvUnit?.eegMastrNummer ? data.pvUnit.eegMastrNummer : null
    );
    if (!!data.location.storageUnits.length) {
      updateFormControl(
        this.formGroup.controls.maStRNumberStorage,
        data.location.storageUnits[0].mastrNummer
      );
    }
    const storageData = data?.location?.storageUnits.length && data?.location?.storageUnits[0];
    this.storageMaStRDisplayData.inbetriebnahmeDatum = storageData.inbetriebnahmeDatum
      ? new Date(storageData?.inbetriebnahmeDatum)
      : null;
    this.storageMaStRDisplayData.registerDate = storageData?.registerDate
      ? new Date(storageData.registerDate)
      : null;
  }

  hasMastrMismatchField(field: string): boolean {
    return this.mastrMismatchFields.includes(field);
  }

  protected readonly startWith = startWith;
}

/** Same calendar day in local time, ignoring clock time (datepicker vs API dates). */
function placeInServiceDatumCalendarKey(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  const d = value instanceof Date ? new Date(value.getTime()) : new Date(value as string | number);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${y}-${pad(m)}-${pad(day)}`;
}

function noP14aChange(initialP14aApplies: boolean): ValidatorFn {
  return (storageAsConsumerControl: AbstractControl<ConsumerModel>): ValidationErrors | null => {
    const currentP14aApplies = p14aApplies('Storage', storageAsConsumerControl.value);
    if (initialP14aApplies !== currentP14aApplies) {
      return currentP14aApplies ? { powerExceeded: true } : { powerDeceeded: true };
    } else return null;
  };
}
